import * as React from 'react';
import {Button, Modal, Table} from 'antd';
import locales from '../locales';
import * as tableUtils from '../utils/table-utils';
import {GridState, searchableSortableColumn} from '../utils/table-utils';
import {ErrorAlert} from '../common/ErrorAlert';
import {ErrorResponse, PageResponseEnvelope, Site} from '../api/dtos/dtos';
import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay
} from '../app/ApplicationContext';
import furoApi from '../api/FuroApi';
import {ColumnProps} from 'antd/lib/table';
import ClientSiteForm from './ClientSiteForm';
import ButtonBar from '../common/ButtonBar';
import ButtonGroup from "antd/lib/button/button-group";
import {showDeleteConfirm, showDeleteSuccessNotification, showSaveSuccessNotification} from "../utils/dialogs";


interface ClientSiteEditorProps {
  clientId: number;
  editable: boolean;
}

interface ClientResponsibleEditorState extends GridState<Site> {
  editedSite?: Site;
  error?: ErrorResponse;
}

class ClientSiteEditor extends React.Component<ClientSiteEditorProps, ClientResponsibleEditorState> {

  public readonly loadingHandler?: LoadingOverlayHandler;

  public state: ClientResponsibleEditorState = {
    ...tableUtils.initialGridState(),
  };

  public componentDidMount() {
    this.reload();
  }

  public render(): React.ReactNode {
    const {editable} = this.props;
    const {data, editedSite, error} = this.state;

    const columns: Array<ColumnProps<Site>> = [
      searchableSortableColumn(locales.address, 'address', this.state, r => r.address),
      searchableSortableColumn(locales.appellation, 'name', this.state, r => r.name),
      {key: 'actions', width: 80, render: this.renderActionColumn}
    ];

    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>

        <Modal
          title={locales.edit_entry}
          visible={editedSite !== undefined}
          footer={null}
          onCancel={this.onFormCancel}
          destroyOnClose={true}
        >
          {editedSite && <ClientSiteForm
              site={editedSite}
              onFormSubmit={this.onFormSubmit}
              onFormCancel={this.onFormCancel}
          />}
        </Modal>

        {editable && <ButtonBar style={{marginBottom: 8}}>
            <Button size="small" type="primary" icon="plus" onClick={this.onAddClick}>{locales.add}</Button>
        </ButtonBar>}

        <Table
          dataSource={data}
          columns={columns}
          rowKey={tableUtils.idRowKeyFn}
          bordered={true}
          size="small"
          pagination={{
            ...tableUtils.defaultPaginationConfig(this.state),
            defaultPageSize: 6,
            showSizeChanger: false,
          }}
          onChange={tableUtils.handleOnGridChange(this.state, this.onGridChange)}
          scroll={{x: 512}}
        />

        <ErrorAlert style={{marginTop: '16px'}} error={error}/>
      </>
    );
  }

  private renderActionColumn = (text: any, record: Site) => {
    const onEditClick = () => this.onEditClick(record);
    const onDeleteClick = () => this.onDeleteClick(record);
    return this.props.editable && (
      <div className="action-column" style={{textAlign: 'right'}}>
        <ButtonGroup>
          <Button icon="edit" onClick={onEditClick} title={locales.edit}/>
          <Button icon="delete" onClick={onDeleteClick} title={locales.delete}/>
        </ButtonGroup>
      </div>
    );
  };

  private onGridChange = (newState: GridState<Site>) => {
    this.setState({...newState}, () => this.reload());
  };

  private reload = () => {
    const {clientId} = this.props;
    const request = tableUtils.toGridRequest(this.state);
    const promise = furoApi.clients.getAllSites(clientId, request)
      .then(this.onReloadSuccess)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onReloadSuccess = (response: PageResponseEnvelope<Site>) => {
    this.setState({
      ...tableUtils.toGridState(response),
      error: undefined,
    });
  };

  private onEditClick = (site: Site) => {
    this.setState({editedSite: site,});
  };

  private onAddClick = () => {
    this.setState({
      editedSite: {
        id: null,
        address: '',
        name: null,
      },
    });
  };

  private onFormCancel = () => {
    this.setState({editedSite: undefined,});
  };

  private onFormSubmit = (site: Site) => {
    const {clientId} = this.props;
    if (site.id === null) {
      // create mode
      withLoadingOverlay(
        furoApi.clients.createSite(clientId, site)
          .then(this.onSaveSuccess)
          .catch(this.onApiError), this.loadingHandler);

    } else {
      // edit mode
      withLoadingOverlay(
        furoApi.clients.saveSite(clientId, site, site.id)
          .then(this.onSaveSuccess)
          .catch(this.onApiError), this.loadingHandler);
    }
  };

  private onSaveSuccess = () => {
    this.setState({editedSite: undefined,});
    showSaveSuccessNotification();
    this.reload();
  };

  private onApiError = (error: ErrorResponse) => {
    this.setState({error});
  };

  private onDeleteClick = (record: Site) => {
    showDeleteConfirm(() => this.deleteContact(record));
  };

  private deleteContact = (site: Site) => {
    const {clientId} = this.props;
    const promise = furoApi.clients.deleteSite(clientId, site.id!!)
      .then(() => {
        showDeleteSuccessNotification();
        this.reload();
      })
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

}

export default ClientSiteEditor;


// WEBPACK FOOTER //
// ./src/client-details/ClientSiteEditor.tsx